// `/integrations/nango/**` — the Nango unified-API connector transport.
//
// MOVED HERE AT ARCFE-1105 by `git mv` from
// `src/features/integrations-connected-apps/nango/client.ts`, now a name-intact re-export shim.
//
// WHY THIS DOOR AND NOT `./ehr`. `/integrations/` resolves to `integrations/ehr`
// by plurality across the domain's doors, but ARCFE-1087 drew the line for this
// door at CHOOSE-or-PROVISION versus USE-a-connected-integration, and a Nango
// connect session provisions a connection. That is the assignment the
// connected-apps ROUTE-OUTSIDE-HUB heal item records, applied.
//
// AND IT ANSWERS HALF OF A QUESTION THE FAMILY RECORD LEFT OPEN. That record's
// blocker says outright: "Whoever carves the door has to decide first whether
// `./connected-apps` means the EHR provisioning flow, the Nango/Merge connector
// surface, or both, because they are two independent data surfaces wearing one
// name." This PR answers BOTH, on the door's own CHOOSE/PROVISION rule — but
// only for TRANSPORT. The 19 Nango and 16 Merge app files, their two cache keys
// and the routines/workflows UI coupling are untouched and stay recorded there.
//
// This module is an OBJECT LITERAL client, not a set of free fns, and it stayed
// one: `apiEndpoints` reads one call per exported symbol, so this file reports 1
// endpoint where it declares 3 — which is exactly why the out-of-hub guard is a
// FILE-grain presence check rather than an endpoint count. Reshaping it into
// three free fns would have been a gratuitous signature change at 4 call sites.
//
// The only edit to the body is the transport accessor: `scribeHttpV2.` ->
// `http().`, where `http()` is this door's registry seam and returns the app's
// same `scribeHttpV2` singleton. Routes, verbs, bodies and the
// `provider_config_key` query param are byte-identical.
import type {
  NangoConnectSessionRequest,
  NangoConnectSessionResponse,
  NangoConnectionsResponse,
  NangoToolPermission,
  NangoToolPermissionsResponse,
  NangoToolPermissionsUpdate,
} from '../../contracts/types';
import { http } from './connectedAppsHttp';

const BASE = '/integrations/nango';

/**
 * Heidi backend proxies Nango with the secret key. The FE never sees
 * `NANGO_SECRET_KEY` — only short-lived connect session tokens.
 *
 * @domain integrations
 */
export const nangoClient = {
  createConnectSession: (body: NangoConnectSessionRequest = {}) =>
    http().post<NangoConnectSessionResponse>(`${BASE}/connect-session`, body),

  listConnections: () => http().get<NangoConnectionsResponse>(`${BASE}/connections`),

  deleteConnection: (connectionId: string, providerConfigKey: string) =>
    http().delete<void>(`${BASE}/connections/${connectionId}`, {
      params: { provider_config_key: providerConfigKey },
    }),

  getToolPermissions: (providerConfigKey: string) =>
    http().get<NangoToolPermissionsResponse>(
      `${BASE}/tool-permissions/${providerConfigKey}`
    ),

  updateToolPermissions: (
    providerConfigKey: string,
    permissions: Record<string, NangoToolPermission>
  ) =>
    http().put<NangoToolPermissionsResponse>(
      `${BASE}/tool-permissions/${providerConfigKey}`,
      { permissions } satisfies NangoToolPermissionsUpdate
    ),
};
